import {
  keepPreviousData,
  useQuery,
  UseQueryOptions,
} from '@tanstack/react-query';
import {
  getComparisonLawPositionsURL,
  getComparisonUcitsPositions,
  ComparisonUcitsPosition,
  fetchFundRiskComplianceData,
  fetchProspectusRestrictionsTimeSeries,
  fetchRiskReportDataKeys,
  fetchStatusChangePage,
  fetchLawPage,
  fetchValueAtRiskPage,
  fetchStressScenarioTests,
  fetchHistoricalData,
  FetchHistoricalDataParams,
  fetchLawHistoricalData,
  FetchLawHistoricalDataParams,
  fetchSrriPage,
  fetchMrmPage,
  fetchShareClassHedgeSummaryPage,
  fetchCollateralStressTestPage,
  fetchFundPerformanceAnalysisPage,
  FetchFundPerformanceAnalysisParams,
  fetchRiskComparisonPage,
  fetchRelativeRiskPage,
  fetchSrriShareClassPage,
  fetchSrriOverviewPage,
  fetchMarketLookbackPage,
  fetchArticle48CoverageRulesPage,
  fetchGlobalExposurePage,
  fetchCssfRiskSpreadingPage,
  fetchCssfInscopePositions,
  fetchSupplementaryRulesPage,
} from '../risk.routes';
import type { HistoricalPageModel } from '../historicalData.types';
import type { SrriPageModel } from '../srri.types';
import type { MrmPageModel } from '../mrm.types';
import type { ShareClassHedgeSummaryPageModel } from '../shareClassHedgeSummary.types';
import type { CollateralStressTestPageModel } from '../collateralStressTest.types';
import type { FundPerformanceAnalysisPageModel } from '../fundPerformanceAnalysis.types';
import type {
  LawName,
  LawPageModel,
  LawHistoricalDataModel,
} from '../law.types';
import type { StatusChangePageModel } from '../statusChange.types';
import type {
  FetchProspectusRestrictionsTimeSeriesParams,
  ProspectusRestrictionsPageModel,
  ProspectusRestrictionsTimeSeriesResponse,
} from '../prospectusRestrictions.types';
import type { ValueAtRiskPageModel } from '../valueAtRisk.types';
import type { StressScenarioPageModel } from '../../../../components/pages/ucits/stressTests/stressScenario.types';
import type { RiskComparisonPageModel } from '../riskComparison.types';
import type { RelativeRiskPageModel } from '../relativeRisk.types';
import type { SrriShareClassPageModel } from '../srriShareClassPage.types';
import type { SrriOverviewPageModel } from '../srriOverview.types';
import type { MarketLookbackPageModel } from '../marketLookback.types';
import type { Article48CoverageRulesPageModel } from '../article48CoverageRules.types';
import type { GlobalExposurePageModel } from '../globalExposure.types';
import type {
  CssfRiskSpreadingPageModel,
  CssfInscopePositionsResponse,
} from '../cssfRiskSpreading.types';
import type { SupplementaryRulesPageModel } from '../supplementaryRules.types';
import { AxiosInstance } from '../../../../utilities/requestClient';
import {
  getReportsListURL,
  getRiskOverviewMonitorURL,
} from '../../../../routes/endpoints/general.routes';
import { RSOverviewPageModel } from '../../../api.types';
import type { RiskOverviewMonitorFundRow } from '../riskOverview.types';

export interface UseStatusChangePageQueryParams {
  fundTypes?: string;
  daysBack: number;
}

export function useStatusChangePageQuery(
  params: UseStatusChangePageQueryParams,
  options?: Omit<
    UseQueryOptions<StatusChangePageModel, Error>,
    'queryKey' | 'queryFn'
  >,
) {
  return useQuery<StatusChangePageModel, Error>({
    queryKey: [
      'risk_decline_and_fall',
      params.fundTypes,
      params.daysBack,
    ] as const,
    queryFn: () => fetchStatusChangePage(params),
    staleTime: 5 * 60 * 1000,
    ...options,
  });
}

export function useLawPageQuery(
  lawName: LawName,
  fundName: string,
  positionDate: string | undefined,
  isHistorical = false,
  options?: Omit<
    UseQueryOptions<LawPageModel, Error>,
    'queryKey' | 'queryFn' | 'enabled'
  >,
) {
  return useQuery<LawPageModel, Error>({
    ...options,
    queryKey: ['law-page', lawName, fundName, positionDate] as const,
    queryFn: () => fetchLawPage(lawName, fundName, positionDate!),
    staleTime: 5 * 60 * 1000,
    enabled: !!lawName && !!fundName && !!positionDate && !isHistorical,
  });
}

export function useValueAtRiskPageQuery(
  fundName: string,
  positionDate: string,
  options?: Omit<
    UseQueryOptions<ValueAtRiskPageModel, Error>,
    'queryKey' | 'queryFn'
  >,
) {
  return useQuery<ValueAtRiskPageModel, Error>({
    queryKey: ['value-at-risk-page', fundName, positionDate] as const,
    queryFn: () => fetchValueAtRiskPage(fundName, positionDate),
    enabled: !!fundName && !!positionDate,
    staleTime: 5 * 60 * 1000,
    ...options,
  });
}

export function useStressScenarioTestsPageModel(
  fundId: string,
  positionDate?: string,
  options?: Omit<
    UseQueryOptions<StressScenarioPageModel, Error>,
    'queryKey' | 'queryFn'
  >,
) {
  return useQuery<StressScenarioPageModel, Error>({
    queryKey: ['stress-scenario-tests', fundId, positionDate],
    queryFn: () => fetchStressScenarioTests(fundId, positionDate!),
    enabled: !!fundId && !!positionDate && (options?.enabled ?? true),
    staleTime: 5 * 60 * 1000,
    ...options,
  });
}

export function useProspectusRestrictionsQuery(
  fundId: string,
  positionDate: string | undefined,
  isHistorical = false,
  options?: Omit<
    UseQueryOptions<ProspectusRestrictionsPageModel, Error>,
    'queryKey' | 'queryFn' | 'enabled'
  >,
) {
  return useQuery<ProspectusRestrictionsPageModel, Error>({
    ...options,
    queryKey: ['prospectus-restrictions', fundId, positionDate] as const,
    queryFn: () => fetchFundRiskComplianceData(fundId, positionDate!),
    staleTime: 5 * 60 * 1000,
    enabled: !!fundId && !!positionDate && !isHistorical,
  });
}

export function useProspectusRestrictionsTimeSeriesQuery(
  params: FetchProspectusRestrictionsTimeSeriesParams | null,
  options?: Omit<
    UseQueryOptions<ProspectusRestrictionsTimeSeriesResponse, Error>,
    'queryKey' | 'queryFn' | 'enabled' | 'placeholderData'
  >,
) {
  const enabled =
    !!params?.fundName &&
    !!params?.ruleName &&
    !!params?.startDate &&
    !!params?.endDate &&
    params.ruleIndex != null &&
    Number.isFinite(params.ruleIndex);

  return useQuery<ProspectusRestrictionsTimeSeriesResponse, Error>({
    ...options,
    queryKey: [
      'prospectus-restrictions-timeseries',
      params?.fundName,
      params?.ruleIndex,
      params?.ruleName,
      params?.startDate,
      params?.endDate,
    ] as const,
    queryFn: () => fetchProspectusRestrictionsTimeSeries(params!),
    staleTime: 5 * 60 * 1000,
    placeholderData: keepPreviousData,
    enabled,
  });
}

export function useCssfRiskSpreadingPageModel(
  fundName: string,
  positionDate?: string,
  options?: Omit<
    UseQueryOptions<CssfRiskSpreadingPageModel, Error>,
    'queryKey' | 'queryFn'
  >,
) {
  return useQuery<CssfRiskSpreadingPageModel, Error>({
    queryKey: ['cssf-risk-spreading', fundName, positionDate],
    queryFn: () => fetchCssfRiskSpreadingPage(fundName, positionDate!),
    enabled: !!fundName && !!positionDate && (options?.enabled ?? true),
    staleTime: 5 * 60 * 1000,
    ...options,
  });
}

export function useCssfInscopePositionsQuery(
  fundName: string,
  ruleNumber: number,
  positionDate?: string,
  options?: Omit<
    UseQueryOptions<CssfInscopePositionsResponse, Error>,
    'queryKey' | 'queryFn'
  >,
) {
  return useQuery<CssfInscopePositionsResponse, Error>({
    queryKey: [
      'cssf-inscope-positions',
      fundName,
      ruleNumber,
      positionDate,
    ],
    queryFn: () =>
      fetchCssfInscopePositions(fundName, ruleNumber, positionDate),
    enabled: !!fundName && !!ruleNumber && (options?.enabled ?? true),
    staleTime: 5 * 60 * 1000,
    ...options,
  });
}

export function useSupplementaryRulesPageQuery(
  fundName: string,
  positionDate: string | undefined,
  options?: Omit<
    UseQueryOptions<SupplementaryRulesPageModel, Error>,
    'queryKey' | 'queryFn' | 'enabled'
  >,
) {
  return useQuery<SupplementaryRulesPageModel, Error>({
    queryKey: ['supplementary-rules-page', fundName, positionDate] as const,
    queryFn: () => fetchSupplementaryRulesPage(fundName, positionDate!),
    enabled: !!fundName && !!positionDate,
    staleTime: 5 * 60 * 1000,
    ...options,
  });
}

export interface UseReportsListQueryParams {
  fundTypes?: string;
  reportType?: string;
  raptorPlatformId?: string;
  status?: string;
}

export type ReportsListByFund = Record<string, string[]>;

export const useReportsListQuery = (
  params: UseReportsListQueryParams,
  options?: Omit<
    UseQueryOptions<ReportsListByFund, Error>,
    'queryKey' | 'queryFn'
  >,
) => {
  return useQuery<ReportsListByFund, Error>({
    queryKey: [
      'reports-list',
      params.fundTypes,
      params.reportType,
      params.raptorPlatformId,
      params.status,
    ],
    queryFn: async () => {
      const url = `/${getReportsListURL(
        params.fundTypes,
        params.reportType,
        params.raptorPlatformId,
        params.status,
      )}`;
      const { data } = await AxiosInstance.get<ReportsListByFund>(url);
      return data;
    },
    enabled: !!params.fundTypes && (options?.enabled ?? true),
    staleTime: 5 * 60 * 1000,
    ...options,
  });
};

export interface UseRiskOverviewMonitorQueryParams {
  positionDate?: string;
  fundTypes?: string;
  raptorPlatformId?: string;
  deceased?: boolean;
}

export const useRiskOverviewMonitorQuery = (
  params: UseRiskOverviewMonitorQueryParams,
  options?: Omit<
    UseQueryOptions<RSOverviewPageModel<RiskOverviewMonitorFundRow[]>, Error>,
    'queryKey' | 'queryFn'
  >,
) => {
  return useQuery<RSOverviewPageModel<RiskOverviewMonitorFundRow[]>, Error>({
    queryKey: [
      'risk-monitor',
      params.positionDate,
      params.fundTypes,
      params.raptorPlatformId,
      params.deceased,
    ],
    queryFn: async () => {
      const url = `/${getRiskOverviewMonitorURL(
        params.positionDate || '',
        params.fundTypes,
        params.raptorPlatformId,
        params.deceased,
      )}`;
      const { data } =
        await AxiosInstance.get<
          RSOverviewPageModel<RiskOverviewMonitorFundRow[]>
        >(url);
      return data;
    },
    enabled: !!params.fundTypes && (options?.enabled ?? true),
    staleTime: 5 * 60 * 1000,
    ...options,
  });
};

export function useRiskReportDataKeysQuery(fundId: string) {
  return useQuery({
    queryKey: ['risk-report-data-keys', fundId],
    queryFn: () => fetchRiskReportDataKeys(fundId),
    staleTime: 60 * 60 * 1000,
    enabled: !!fundId && fundId !== 'none_selected',
  });
}

function isHistoricalDataQueryEnabled(
  params: FetchHistoricalDataParams,
): boolean {
  return (
    !!params.fundName &&
    params.fundName !== 'none_selected' &&
    !!params.date1 &&
    !!params.date2 &&
    !!params.key1 &&
    params.key1 !== 'none_selected' &&
    !!params.key2 &&
    params.key2 !== 'none_selected'
  );
}

export function useHistoricalDataQuery(
  params: FetchHistoricalDataParams,
  options?: Omit<
    UseQueryOptions<HistoricalPageModel, Error>,
    'queryKey' | 'queryFn'
  >,
) {
  return useQuery<HistoricalPageModel, Error>({
    queryKey: [
      'historical-data',
      params.fundName,
      params.date1,
      params.date2,
      params.key1,
      params.key2,
    ] as const,
    queryFn: () => fetchHistoricalData(params),
    enabled: isHistoricalDataQueryEnabled(params) && (options?.enabled ?? true),
    staleTime: 5 * 60 * 1000,
    ...options,
  });
}

export function useLawHistoricalDataQuery(
  params: FetchLawHistoricalDataParams,
  options?: Omit<
    UseQueryOptions<LawHistoricalDataModel, Error>,
    'queryKey' | 'queryFn'
  >,
) {
  return useQuery<LawHistoricalDataModel, Error>({
    queryKey: [
      'law-historical-data',
      params.fundName,
      params.startDate,
      params.endDate,
    ] as const,
    queryFn: () => fetchLawHistoricalData(params),
    enabled:
      !!params.fundName &&
      params.fundName !== 'none_selected' &&
      !!params.startDate &&
      !!params.endDate &&
      (options?.enabled ?? true),
    staleTime: 5 * 60 * 1000,
    ...options,
  });
}

export function useSrriPageQuery(
  fundName: string,
  gmidShareclass?: string,
  options?: Omit<UseQueryOptions<SrriPageModel, Error>, 'queryKey' | 'queryFn'>,
) {
  return useQuery<SrriPageModel, Error>({
    queryKey: ['srri-page', fundName, gmidShareclass] as const,
    queryFn: () =>
      fetchSrriPage({
        fundName,
        gmidShareclass,
      }),
    enabled:
      !!fundName &&
      fundName !== 'none_selected' &&
      (gmidShareclass === undefined || !!gmidShareclass) &&
      (options?.enabled ?? true),
    staleTime: 5 * 60 * 1000,
    ...options,
  });
}

export function useMrmPageQuery(
  fundName: string,
  options?: Omit<UseQueryOptions<MrmPageModel, Error>, 'queryKey' | 'queryFn'>,
) {
  return useQuery<MrmPageModel, Error>({
    queryKey: ['mrm-page', fundName] as const,
    queryFn: () => fetchMrmPage(fundName),
    enabled:
      !!fundName && fundName !== 'none_selected' && (options?.enabled ?? true),
    staleTime: 5 * 60 * 1000,
    ...options,
  });
}

export interface UseShareClassHedgeSummaryPageQueryParams {
  positionDate: string;
  fundTypes?: string;
}

export function useShareClassHedgeSummaryPageQuery(
  params: UseShareClassHedgeSummaryPageQueryParams,
  options?: Omit<
    UseQueryOptions<ShareClassHedgeSummaryPageModel, Error>,
    'queryKey' | 'queryFn'
  >,
) {
  return useQuery<ShareClassHedgeSummaryPageModel, Error>({
    queryKey: [
      'get-hedge-ratios',
      params.positionDate,
      params.fundTypes,
    ] as const,
    queryFn: () => fetchShareClassHedgeSummaryPage(params),
    enabled:
      !!params.positionDate &&
      !!params.fundTypes?.length &&
      (options?.enabled ?? true),
    staleTime: 5 * 60 * 1000,
    ...options,
  });
}

export function useCollateralStressTestPageQuery(
  fundName: string,
  positionDate: string,
  options?: Omit<
    UseQueryOptions<CollateralStressTestPageModel, Error>,
    'queryKey' | 'queryFn'
  >,
) {
  return useQuery<CollateralStressTestPageModel, Error>({
    queryKey: ['collateral-stress-test-page', fundName, positionDate] as const,
    queryFn: () => fetchCollateralStressTestPage(fundName),
    enabled:
      !!fundName && fundName !== 'none_selected' && (options?.enabled ?? true),
    staleTime: 5 * 60 * 1000,
    ...options,
  });
}

export function useFundPerformanceAnalysisPageQuery(
  params: FetchFundPerformanceAnalysisParams,
  options?: Omit<
    UseQueryOptions<FundPerformanceAnalysisPageModel, Error>,
    'queryKey' | 'queryFn'
  >,
) {
  return useQuery<FundPerformanceAnalysisPageModel, Error>({
    queryKey: [
      'fund-performance-analysis',
      params.fundName,
      params.date,
      params.aperiod,
    ] as const,
    queryFn: () => fetchFundPerformanceAnalysisPage(params),
    enabled:
      !!params.fundName &&
      params.fundName !== 'none_selected' &&
      !!params.date &&
      !!params.aperiod &&
      (options?.enabled ?? true),
    staleTime: 5 * 60 * 1000,
    ...options,
  });
}

export function useRiskComparisonPageQuery(
  fundName: string,
  fromDate: string | null,
  toDate: string | null,
  options?: Omit<
    UseQueryOptions<RiskComparisonPageModel, Error>,
    'queryKey' | 'queryFn'
  >,
) {
  return useQuery<RiskComparisonPageModel, Error>({
    queryKey: ['risk-comparison-page', fundName, fromDate, toDate] as const,
    queryFn: () => fetchRiskComparisonPage(fundName, fromDate!, toDate!),
    enabled:
      !!fundName &&
      fundName !== 'none_selected' &&
      !!fromDate &&
      !!toDate &&
      (options?.enabled ?? true),
    staleTime: 5 * 60 * 1000,
    ...options,
  });
}

export function useRelativeRiskPageQuery(
  fundName: string,
  positionDate: string | undefined,
  options?: Omit<
    UseQueryOptions<RelativeRiskPageModel, Error>,
    'queryKey' | 'queryFn'
  >,
) {
  return useQuery<RelativeRiskPageModel, Error>({
    queryKey: ['relative-risk-page', fundName, positionDate] as const,
    queryFn: () => fetchRelativeRiskPage(fundName, positionDate!),
    enabled:
      !!fundName &&
      fundName !== 'none_selected' &&
      !!positionDate &&
      (options?.enabled ?? true),
    staleTime: 5 * 60 * 1000,
    ...options,
  });
}

export function useSrriShareClassPageQuery(
  fundName: string,
  options?: Omit<
    UseQueryOptions<SrriShareClassPageModel, Error>,
    'queryKey' | 'queryFn'
  >,
) {
  return useQuery<SrriShareClassPageModel, Error>({
    queryKey: ['get-srri-share-class-page', fundName] as const,
    queryFn: () => fetchSrriShareClassPage(fundName),
    enabled:
      !!fundName && fundName !== 'none_selected' && (options?.enabled ?? true),
    staleTime: 5 * 60 * 1000,
    ...options,
  });
}

export function useSrriOverviewPageQuery(
  positionDate: string,
  options?: Omit<
    UseQueryOptions<SrriOverviewPageModel, Error>,
    'queryKey' | 'queryFn'
  >,
) {
  return useQuery<SrriOverviewPageModel, Error>({
    queryKey: ['srri-overview-page', positionDate] as const,
    queryFn: () => fetchSrriOverviewPage(positionDate),
    enabled: !!positionDate && (options?.enabled ?? true),
    staleTime: 5 * 60 * 1000,
    ...options,
  });
}

export function useMarketLookbackPageQuery(
  selectedDate: string,
  lookback: string,
  options?: Omit<
    UseQueryOptions<MarketLookbackPageModel, Error>,
    'queryKey' | 'queryFn'
  >,
) {
  return useQuery<MarketLookbackPageModel, Error>({
    queryKey: ['market-lookback', selectedDate, lookback] as const,
    queryFn: () => fetchMarketLookbackPage(selectedDate, lookback),
    enabled:
      !!selectedDate && !!lookback && (options?.enabled ?? true),
    staleTime: 5 * 60 * 1000,
    ...options,
  });
}

export function useArticle48CoverageRulesPageQuery(
  options?: Omit<
    UseQueryOptions<Article48CoverageRulesPageModel, Error>,
    'queryKey' | 'queryFn'
  >,
) {
  return useQuery<Article48CoverageRulesPageModel, Error>({
    queryKey: ['article-48-coverage-rules'] as const,
    queryFn: () => fetchArticle48CoverageRulesPage(),
    enabled: options?.enabled ?? true,
    staleTime: 5 * 60 * 1000,
    ...options,
  });
}

export function useGlobalExposurePageQuery(
  positionDate?: string,
  options?: Omit<
    UseQueryOptions<GlobalExposurePageModel, Error>,
    'queryKey' | 'queryFn'
  >,
) {
  return useQuery<GlobalExposurePageModel, Error>({
    queryKey: ['global-exposure', positionDate] as const,
    queryFn: () => fetchGlobalExposurePage(positionDate),
    enabled: options?.enabled ?? true,
    staleTime: 5 * 60 * 1000,
    ...options,
  });
}

interface ComparisonUcitsPositionsParams {
  fundId?: string;
  issuer?: string;
  activePositionDate?: string;
}

export const useComparisonUcitsPositions = (
  params: ComparisonUcitsPositionsParams,
  options?: Omit<
    UseQueryOptions<ComparisonUcitsPosition[], Error>,
    'queryKey' | 'queryFn'
  >,
) => {
  return useQuery<ComparisonUcitsPosition[], Error>({
    queryKey: [
      'comparison-ucits-positions',
      params.fundId,
      params.issuer,
      params.activePositionDate,
    ],
    queryFn: () =>
      getComparisonUcitsPositions({
        fundId: params.fundId!,
        issuer: params.issuer!,
        activePositionDate: params.activePositionDate!,
      }),
    enabled:
      !!params.fundId &&
      !!params.issuer &&
      !!params.activePositionDate &&
      (options?.enabled ?? true),
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...options,
  });
};

export type LawComparisonFundType =
  | 'ucits'
  | 'qiaif'
  | 'partii'
  | 'riaif'
  | 'nurs'
  | 'fivg'
  | 'act40';

export interface ComparisonLawPosition {
  mr_position: number;
  exposure: number;
  selected_position_date?: string;
  returned_position_date?: string;
  [key: string]: any;
}

interface ComparisonLawPositionsParams {
  fundType: LawComparisonFundType;
  fundId?: string;
  issuer?: string;
  activePositionDate?: string;
  /**
   * Optional override in case the backend uses a different fund prefix
   * in the URL than the UI fund type.
   */
  endpointFundType?: LawComparisonFundType;
}

export const useComparisonLawPositions = (
  params: ComparisonLawPositionsParams,
  options?: Omit<
    UseQueryOptions<ComparisonLawPosition[], Error>,
    'queryKey' | 'queryFn'
  >,
) => {
  const endpointFundType = params.endpointFundType ?? params.fundType;

  return useQuery<ComparisonLawPosition[], Error>({
    queryKey: [
      'comparison-law-positions',
      params.fundType,
      endpointFundType,
      params.fundId,
      params.issuer,
      params.activePositionDate,
    ],
    queryFn: async () => {
      const { data } = await AxiosInstance.get<ComparisonLawPosition[]>(
        getComparisonLawPositionsURL(
          endpointFundType,
          params.fundId!,
          params.issuer!,
          params.activePositionDate!,
        ),
      );
      return data;
    },
    enabled:
      !!params.fundId &&
      !!params.issuer &&
      !!params.activePositionDate &&
      (options?.enabled ?? true),
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...options,
  });
};
